/* eslint-disable camelcase */
import React, { useState, useEffect, useCallback, ElementType } from 'react';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  Heading,
  Image,
  Input,
  Select,
  Switch,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  useToast,
  Badge,
  HStack,
  Text,
  VStack,
  useColorModeValue,
  Icon,
} from '@chakra-ui/react';
import 'leaflet/dist/leaflet.css';
import {
  useGetCarrierQuery,
  Carrier,
  useUpdateCarrierByPkMutation,
  useGetCarrierActiveOrdersQuery,
  useGetCarrierDeliveredOrdersQuery,
  Order,
} from '../utils/__generated__/graphql';
import { useParams, useNavigate } from 'react-router-dom';
import { nhost } from 'src/lib/nhost';
import { useGetImageUrl } from 'src/hooks';
import { Field, labelMap, LoadingSpinner, ModernCard, OrderCard, PageHeader } from './shared';
import { calculateDateRange, convertSnakeCaseToWords, timeRanges } from 'src/utils';
import { useUserDefaultRole } from '@nhost/react';
import { FiArrowLeft, FiEdit3, FiTruck } from 'react-icons/fi';
import SearchDropdown from './shared/SearchDropdown';

const fieldNames = ['isOnline', 'name', 'address', 'phone', 'verification_status', 'restaurantId'];

export const EditCarrier = () => {
  const { id: carrierId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const userRole = useUserDefaultRole();
  const [activeTab, setActiveTab] = useState(() => {
    const savedTab = localStorage.getItem('editCarrierActiveTab');
    return savedTab ? Number(savedTab) : 0;
  });
  const [timeRange, setTimeRange] = useState<string>(() => {
    return localStorage.getItem('timeRange') || 'today';
  });
  const { startDate, endDate } = calculateDateRange(timeRange);

  const {
    data: carrier,
    loading: carrierLoading,
    error: carrierError,
    refetch,
  } = useGetCarrierQuery({
    variables: { carrierId },
  });

  const carrierData = carrier?.carrier_by_pk as Carrier;

  const { data: activeOrders } = useGetCarrierActiveOrdersQuery({
    variables: { carrierId },
  });

  const { data: deliveredOrders } = useGetCarrierDeliveredOrdersQuery({
    variables: { carrierId, startDate, endDate },
  });

  // const { upload } = useFileUpload();

  const [updateCarrier, { loading: updateLoading, error: updateError }] = useUpdateCarrierByPkMutation();

  const [formValues, setFormValues] = useState<Field[]>(
    fieldNames?.map((fieldName) => ({
      label: fieldName,
      value: '',
    }))
  );

  const { imageUrl: imageUrl } = useGetImageUrl(carrierData?.image);

  // const { imageUrl: coverUrl } = useGetImageUrl(carrierData?.cover);

  const handleTimeRangeChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const newTimeRange = e.target.value;

      setTimeRange(newTimeRange);
      localStorage.setItem('timeRange', newTimeRange);
    },
    [setTimeRange]
  );

  const handleActiveTab = useCallback(
    (e: number) => {
      setActiveTab(e);
      localStorage.setItem('editCarrierActiveTab', e.toString());
    },
    [setActiveTab]
  );

  useEffect(() => {
    if (carrierData) {
      setFormValues((prevValues) =>
        prevValues?.map((field) => ({
          ...field,
          value: (carrierData as any)[field.label],
        }))
      );
    }
  }, [carrierData]);

  const handleInputChange =
    (label: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      const { value } = e.target;

      // Handle null values for restaurantId
      const processedValue = label === 'restaurantId' && value === null ? null : value;

      setFormValues((prevValues) =>
        prevValues?.map((field) => (field.label === label ? { ...field, value: processedValue } : field))
      );
    };

  const hasFormChanged = () => {
    return formValues.some((field) => {
      const originalValue = (carrierData as any)[field.label];
      const currentValue = field.value;

      // Handle null comparison for restaurantId
      if (field.label === 'restaurantId') {
        return currentValue !== originalValue;
      }

      return currentValue !== (originalValue || '');
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!hasFormChanged()) {
      return;
    }

    const updatedValues = formValues.reduce(
      (acc, field) => {
        // Handle null values properly
        if (field.value === null) {
          acc[field.label] = null;
        } else {
          acc[field.label] = field.value;
        }
        return acc;
      },
      {} as Record<string, string | number | null>
    );

    try {
      await updateCarrier({
        variables: {
          pk_columns: { id: carrierId },
          _set: updatedValues,
        },
      });
      toast({
        title: 'Carrier Updated successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      refetch();
    } catch (error: any) {
      toast({
        title: 'Failed to add Carrier',
        description: error?.message || 'An error occurred',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      console.error('Error updating Carrier:', error?.message);
    }
  };

  const handleFileChange = async (event: any, type: string) => {
    const file = event.target.files[0];
    const previousFileId = carrierData?.image;

    try {
      // Delete previous file if it exists
      if (previousFileId) {
        try {
          await nhost.storage.delete({ fileId: previousFileId });
        } catch (deleteError) {
          console.error('Error deleting previous file:', deleteError);
        }
      }

      // Compress the image before uploading
      const compressedFile = await new Promise<File>((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const img = document.createElement('img');
          img.onload = () => {
            const canvas = document.createElement('canvas');
            const MAX_WIDTH = 800;
            const MAX_HEIGHT = 800;
            let width = img.width;
            let height = img.height;

            if (width > height) {
              if (width > MAX_WIDTH) {
                height *= MAX_WIDTH / width;
                width = MAX_WIDTH;
              }
            } else {
              if (height > MAX_HEIGHT) {
                width *= MAX_HEIGHT / height;
                height = MAX_HEIGHT;
              }
            }

            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            ctx?.drawImage(img, 0, 0, width, height);

            canvas.toBlob(
              (blob) => {
                if (blob) {
                  resolve(new File([blob], file.name, { type: 'image/jpeg' }));
                }
              },
              'image/jpeg',
              0.7 // Compression quality (0.7 = 70% quality)
            );
          };
          img.src = e.target?.result as string;
        };
        reader.readAsDataURL(file);
      });

      // Upload the compressed file
      const result = await nhost.storage.upload({
        file: compressedFile,
        bucketId: 'default',
      });

      if (result) {
        const imageType = 'image';
        await updateCarrier({
          variables: {
            pk_columns: { id: carrierId },
            _set: {
              [imageType]: result?.fileMetadata?.id ?? null,
            },
          },
        });

        refetch();
      }
    } catch (error: any) {
      console.error(`Error uploading ${type}:`, error?.message);
    }
  };

  if (carrierLoading) return <LoadingSpinner text="Loading carrier details..." />;
  if (carrierError) {
    toast({
      title: 'Error',
      description: carrierError?.message || 'Failed to load carrier details',
      status: 'error',
      duration: 5000,
      isClosable: true,
    });
    return (
      <ModernCard p={8}>
        <VStack spacing={4} textAlign="center">
          <Text fontSize="lg" fontWeight="semibold" color="red.500">
            Error Loading Carrier Details
          </Text>
          <Text color="gray.600">{carrierError?.message}</Text>
        </VStack>
      </ModernCard>
    );
  }

  if (updateLoading) return <LoadingSpinner text="Updating carrier..." />;
  if (updateError) {
    toast({
      title: 'Error',
      description: updateError.message || 'Failed to update carrier',
      status: 'error',
      duration: 5000,
      isClosable: true,
    });
  }

  return (
    <>
      <PageHeader
        title="Carrier Details"
        subtitle={`Managing ${carrierData?.name || 'carrier'} information`}
        actions={
          <Button
            leftIcon={<Icon as={FiArrowLeft as ElementType} boxSize={5} />}
            colorScheme="gray"
            variant="outline"
            onClick={() => navigate(-1)}
            size={{ base: 'md', md: 'lg' }}
          >
            <Text display={{ base: 'none', sm: 'block' }}>Back to Carriers</Text>
            <Text display={{ base: 'block', sm: 'none' }}>Back</Text>
          </Button>
        }
      />

      <Tabs
        size={{ base: 'sm', md: 'md' }}
        variant="soft-rounded"
        colorScheme="brand"
        index={activeTab}
        onChange={handleActiveTab}
        p={{ base: 4, md: 6 }}
      >
        <Box
          borderRadius="xl"
          overflow="hidden"
          backgroundColor={useColorModeValue('white', 'gray.800')}
          shadow="sm"
          pt={6}
          px={4}
          alignItems="center"
          display="flex"
        >
          <TabList mb={{ base: 4, md: 6 }}>
            <Tab
              _selected={{ color: 'white', bg: 'brand.500' }}
              fontWeight="medium"
              fontSize={{ base: 'sm', md: 'md' }}
            >
              <HStack spacing={2}>
                <Icon as={FiEdit3 as ElementType} boxSize={{ base: 4, md: 5 }} />
                <Text>Carrier Details</Text>
              </HStack>
            </Tab>
            <Tab
              _selected={{ color: 'white', bg: 'brand.500' }}
              fontWeight="medium"
              fontSize={{ base: 'sm', md: 'md' }}
            >
              <HStack spacing={2}>
                <Icon as={FiTruck as ElementType} boxSize={{ base: 4, md: 5 }} />
                <Text>Active Deliveries</Text>
              </HStack>
            </Tab>
            <Tab
              _selected={{ color: 'white', bg: 'brand.500' }}
              fontWeight="medium"
              fontSize={{ base: 'sm', md: 'md' }}
            >
              <HStack spacing={2}>
                <Icon as={FiTruck as ElementType} boxSize={{ base: 4, md: 5 }} />
                <Text>Completed Deliveries</Text>
              </HStack>
            </Tab>
          </TabList>
        </Box>
        <TabPanels>
          <TabPanel>
            <VStack spacing={6} align="stretch">
              {/* Profile Image Section */}
              <ModernCard p={6}>
                <VStack spacing={4} align="stretch">
                  <Heading size="md" color="gray.700" mb={2}>
                    Profile Image
                  </Heading>
                  <Flex direction={{ base: 'column', md: 'row' }} gap={6} align="start">
                    <Box position="relative">
                      <Image
                        objectFit="cover"
                        maxHeight={200}
                        width="200px"
                        aspectRatio={1 / 1}
                        src={imageUrl}
                        alt={`Carrier ${carrierData?.name}`}
                        borderRadius="lg"
                        border="2px solid"
                        borderColor="gray.200"
                        _hover={{ borderColor: 'blue.300' }}
                        transition="all 0.2s"
                      />
                      <Box position="absolute" top={2} right={2} bg="white" borderRadius="full" p={1} boxShadow="md">
                        <Icon as={FiEdit3 as ElementType} color="blue.500" />
                      </Box>
                    </Box>
                    <VStack align="start" spacing={3} flex={1}>
                      <Text fontSize="sm" color="gray.600" fontWeight="medium">
                        Upload a new profile image
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        Recommended: Square image, 400x400 pixels or larger
                      </Text>
                      <Button
                        size="sm"
                        colorScheme="blue"
                        variant="outline"
                        onClick={() => document.getElementById('profile-image-input')?.click()}
                      >
                        Choose Image
                      </Button>
                      <input
                        id="profile-image-input"
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={(e) => handleFileChange(e, 'image')}
                      />
                    </VStack>
                  </Flex>
                </VStack>
              </ModernCard>

              {/* Basic Information Section */}
              <ModernCard p={6}>
                <VStack spacing={6} align="stretch">
                  <Heading size="md" color="gray.700" mb={2}>
                    Basic Information
                  </Heading>
                  <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={6}>
                    {formValues
                      .filter((field) => ['name', 'phone', 'address'].includes(field.label))
                      .map((field) => (
                        <FormControl key={field.label}>
                          <FormLabel fontWeight="medium" color="gray.700">
                            {labelMap[field.label] || convertSnakeCaseToWords(field.label)}
                          </FormLabel>
                          <Input
                            type={field.label === 'phone' ? 'tel' : 'text'}
                            name={field?.label}
                            value={field?.value as string}
                            onChange={handleInputChange(field.label)}
                            size="lg"
                            borderRadius="md"
                            borderColor="gray.300"
                            _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)' }}
                            _hover={{ borderColor: 'gray.400' }}
                          />
                        </FormControl>
                      ))}
                    <Text fontSize="sm" color="gray.600" fontWeight="medium">
                      Email: {carrierData?.user?.email || 'N/A'}
                    </Text>
                  </Grid>
                </VStack>
              </ModernCard>

              {/* Status & Settings Section */}
              <ModernCard p={6}>
                <VStack spacing={6} align="stretch">
                  <Heading size="md" color="gray.700" mb={2}>
                    Status & Settings
                  </Heading>
                  <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={6}>
                    {/* Online Status */}
                    <FormControl>
                      <FormLabel fontWeight="medium" color="gray.700">
                        Online Status
                      </FormLabel>
                      <Flex align="center" gap={3} p={4} bg="gray.50" borderRadius="md">
                        <Switch
                          name="isOnline"
                          isChecked={formValues.find((f) => f.label === 'isOnline')?.value}
                          size="lg"
                          colorScheme="green"
                          onChange={(e) => {
                            const syntheticEvent = {
                              target: {
                                value: e.target.checked,
                              },
                            } as unknown as React.ChangeEvent<
                              HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
                            >;
                            handleInputChange('isOnline')(syntheticEvent);
                          }}
                        />
                        <VStack align="start" spacing={0}>
                          <Text fontWeight="medium" fontSize="sm">
                            {formValues.find((f) => f.label === 'isOnline')?.value ? 'Online' : 'Offline'}
                          </Text>
                          <Text fontSize="xs" color="gray.500">
                            {formValues.find((f) => f.label === 'isOnline')?.value
                              ? 'Available for orders'
                              : 'Not available'}
                          </Text>
                        </VStack>
                      </Flex>
                    </FormControl>

                    {/* Verification Status */}
                    <FormControl>
                      <FormLabel fontWeight="medium" color="gray.700">
                        Verification Status
                      </FormLabel>
                      <Select
                        name="verification_status"
                        value={(formValues.find((f) => f.label === 'verification_status')?.value as string) || ''}
                        onChange={handleInputChange('verification_status')}
                        size="lg"
                        borderRadius="md"
                        borderColor="gray.300"
                        _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)' }}
                      >
                        <option value="">Select Status</option>
                        <option value="pending">Pending</option>
                        <option value="reviewing">Reviewing</option>
                        <option value="verified">Verified</option>
                        <option value="rejected">Rejected</option>
                        <option value="conditional">Conditional</option>
                        <option value="processing">Processing</option>
                      </Select>
                    </FormControl>
                  </Grid>
                </VStack>
              </ModernCard>

              {/* Restaurant Assignment Section */}
              <ModernCard p={6}>
                <VStack spacing={6} align="stretch">
                  <Heading size="md" color="gray.700" mb={2}>
                    Restaurant Assignment
                  </Heading>
                  <VStack spacing={4} align="stretch">
                    <Box p={4} bg="blue.50" borderRadius="md" border="1px solid" borderColor="blue.200">
                      <Text fontSize="sm" color="blue.700" fontWeight="medium">
                        Current Restaurant: {carrierData?.restaurant?.name || 'None Assigned'}
                      </Text>
                    </Box>
                    <Flex direction={{ base: 'column', md: 'row' }} gap={3} align="start">
                      <Box flex={1}>
                        <Text fontSize="sm" color="gray.600" fontWeight="medium" mb={2}>
                          Search and select a restaurant
                        </Text>
                        <SearchDropdown
                          handleChange={(newValue: string) =>
                            handleInputChange('restaurantId')({ target: { value: newValue } } as any)
                          }
                        />
                      </Box>
                      <Button
                        size="lg"
                        colorScheme="red"
                        variant="outline"
                        onClick={() => handleInputChange('restaurantId')({ target: { value: null } } as any)}
                        _hover={{ bg: 'red.50' }}
                      >
                        Clear Assignment
                      </Button>
                    </Flex>
                  </VStack>
                </VStack>
              </ModernCard>

              {/* Current Status Display */}
              {carrierData?.verification_status && (
                <ModernCard p={6}>
                  <VStack spacing={4} align="stretch">
                    <Heading size="md" color="gray.700" mb={2}>
                      Current Status
                    </Heading>
                    <Flex align="center" gap={4} p={4} bg="gray.50" borderRadius="md">
                      <Icon as={FiTruck as ElementType} color="blue.500" boxSize={5} />
                      <VStack align="start" spacing={1}>
                        <Text fontWeight="medium" fontSize="sm">
                          Verification Status
                        </Text>
                        <Badge
                          colorScheme={
                            carrierData.verification_status === 'verified'
                              ? 'green'
                              : carrierData.verification_status === 'rejected'
                                ? 'red'
                                : carrierData.verification_status === 'conditional'
                                  ? 'orange'
                                  : carrierData.verification_status === 'reviewing'
                                    ? 'blue'
                                    : carrierData.verification_status === 'processing'
                                      ? 'purple'
                                      : 'gray'
                          }
                          fontSize="sm"
                          px={3}
                          py={1}
                          borderRadius="full"
                        >
                          {carrierData.verification_status.charAt(0).toUpperCase() +
                            carrierData.verification_status.slice(1)}
                        </Badge>
                      </VStack>
                    </Flex>
                  </VStack>
                </ModernCard>
              )}

              {/* Action Buttons */}
              <Flex justifyContent="flex-end" gap={4}>
                <Button variant="outline" size="lg" onClick={() => navigate(-1)} _hover={{ bg: 'gray.50' }}>
                  Cancel
                </Button>
                <Button
                  colorScheme="blue"
                  size="lg"
                  onClick={handleSubmit}
                  leftIcon={<Icon as={FiEdit3 as ElementType} boxSize={5} />}
                  _hover={{ transform: 'translateY(-1px)', boxShadow: 'lg' }}
                  isLoading={updateLoading}
                  loadingText="Updating..."
                >
                  Update Carrier
                </Button>
              </Flex>
            </VStack>
          </TabPanel>

          <TabPanel>
            <Grid templateColumns="repeat(2, 1fr)" gap={4}>
              {activeOrders?.order.map((order) => (
                <OrderCard key={order.id} order={order as Order} userRole={userRole} />
              ))}
            </Grid>
          </TabPanel>

          <TabPanel>
            <Select mb="6" placeholder="Select time range" mt={4} value={timeRange} onChange={handleTimeRangeChange}>
              {timeRanges.map((range) => (
                <option key={range.value} value={range.value}>
                  {range.label}
                </option>
              ))}
            </Select>
            <Grid templateColumns="repeat(2, 1fr)" gap={4}>
              {deliveredOrders?.order.map((order) => (
                <OrderCard key={order.id} order={order as Order} userRole={userRole} />
              ))}
            </Grid>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </>
  );
};
